import React, { useState } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../components/Modal";
import { validateLedgerTemplateAction } from "../../../../actions/validateLedgerTemplateAction";
import { insertLedgerDetailsAction } from "../../../../actions/insertLedgerDetailsAction";
import "./index.scss";

const ValidateLedgerModal = ({
  isOpen,
  onClose,
  selectedEntities = [],
  onSuccess,
}) => {
  const [file, setFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setFile(null);
    setValidationResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setValidationResult(null);
    setValidating(true);
    const result = await validateLedgerTemplateAction(f);
    setValidating(false);
    if (result.success) {
      setValidationResult(result.data);
      toast.success(result.message || "Validation completed");
    } else {
      toast.error(result.message || "Validation failed");
      setFile(null);
    }
  };

  const handleConfirmSave = async () => {
    if (!validationResult || !allValid) return;
    setSaving(true);

    // Build ledgerInsertRequests from validated rows
    const ledgerInsertRequests = validationResult.map((row) => ({
      entityId: row.entityId || "",
      companyName: row.companyName || "",
      ledgerName: row.ledgerName || "",
      gstNumbers: row.gstNumbers || "",
    }));

    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
    const createdBy = userInfo.name || userInfo.username || "Admin";

    const result = await insertLedgerDetailsAction(
      ledgerInsertRequests,
      createdBy,
    );
    setSaving(false);

    if (result.success) {
      toast.success(result.message || "Ledger details saved successfully!");
      if (onSuccess) onSuccess();
      handleClose();
    } else {
      toast.error(result.message || "Failed to save ledger details");
    }
  };

  const validCount = validationResult?.filter((r) => r.isValid).length ?? 0;
  const invalidCount = validationResult
    ? validationResult.length - validCount
    : 0;
  const allValid =
    validationResult && validationResult.length > 0 && invalidCount === 0;
  const canSave = !!(validationResult && allValid && !saving && !validating);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Validate Ledger Template"
      size="medium"
    >
      <div
        className="vlm-body"
        style={{
          padding: "15px 30px",
        }}
      >
        {/* ── Upload zone ── */}
        <div className="vlm-upload-section">
          <label className="vlm-upload-label" htmlFor="vlm-file-input">
            <input
              id="vlm-file-input"
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <div
              className={`vlm-upload-zone ${file ? (validationResult ? (allValid ? "vlm-upload-zone--valid" : "vlm-upload-zone--warn") : "vlm-upload-zone--selected") : ""}`}
            >
              {validating ? (
                <>
                  <div className="vlm-upload-zone__spinner" />
                  <span className="vlm-upload-zone__text">
                    Validating file…
                  </span>
                </>
              ) : file ? (
                <>
                  <span className="vlm-upload-zone__icon">📎</span>
                  <div className="vlm-upload-zone__info">
                    <span className="vlm-upload-zone__filename">
                      {file.name}
                    </span>
                    {validationResult && (
                      <span
                        className={`vlm-upload-zone__result ${allValid ? "vlm-upload-zone__result--ok" : "vlm-upload-zone__result--warn"}`}
                      >
                        {allValid
                          ? "✓ All entries valid"
                          : `⚠ ${invalidCount} issue${invalidCount !== 1 ? "s" : ""} found`}
                      </span>
                    )}
                  </div>
                  <span className="vlm-upload-zone__change">
                    Click to change
                  </span>
                </>
              ) : (
                <>
                  <svg
                    className="vlm-upload-zone__icon-svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="16 16 12 12 8 16" />
                    <line x1="12" y1="12" x2="12" y2="21" />
                    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                  </svg>
                  <span className="vlm-upload-zone__text">
                    Click to upload Excel file
                  </span>
                  <span className="vlm-upload-zone__hint">.xlsx or .xls</span>
                </>
              )}
            </div>
          </label>
        </div>

        {/* ── Summary pills ── */}
        {validationResult && (
          <div className="vlm-summary">
            <div className="vlm-summary__pill vlm-summary__pill--total">
              <span className="vlm-summary__num">
                {validationResult.length}
              </span>
              <span className="vlm-summary__lbl">Total</span>
            </div>
            <div className="vlm-summary__pill vlm-summary__pill--valid">
              <span className="vlm-summary__num">{validCount}</span>
              <span className="vlm-summary__lbl">Valid</span>
            </div>
            <div className="vlm-summary__pill vlm-summary__pill--invalid">
              <span className="vlm-summary__num">{invalidCount}</span>
              <span className="vlm-summary__lbl">Issues</span>
            </div>
          </div>
        )}

        {/* ── Result table ── */}
        {validationResult && validationResult.length > 0 && (
          <div className="vlm-table-section">
            <div className="vlm-table-wrap">
              <table className="vlm-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Entity ID</th>
                    <th>Company Name</th>
                    <th>Ledger Name</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {validationResult.map((row, i) => {
                    const isOk = row.isValid === true;
                    return (
                      <tr
                        key={i}
                        className={isOk ? "vlm-row--ok" : "vlm-row--error"}
                      >
                        <td className="vlm-td--num">{i + 1}</td>
                        <td>
                          <span className="vlm-entity-id">
                            {row.entityId || "—"}
                          </span>
                        </td>
                        <td>{row.companyName || "—"}</td>
                        <td>
                          <span className="vlm-ledger-name">
                            {row.ledgerName || "—"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`vlm-badge ${isOk ? "vlm-badge--success" : "vlm-badge--error"}`}
                          >
                            {isOk ? "✓ Valid" : `✗ ${row.validationMessage}`}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Footer actions ── */}
        <div className="vlm-footer">
          <button
            className="vlm-btn vlm-btn--cancel"
            onClick={handleClose}
            disabled={saving}
          >
            Close
          </button>
          <button
            className="vlm-btn vlm-btn--submit"
            disabled={!canSave}
            onClick={handleConfirmSave}
          >
            {saving ? (
              <>
                <span className="vlm-btn__spinner" />
                Saving…
              </>
            ) : (
              "✓ Confirm & Save"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ValidateLedgerModal;
